// src/services/classroomWaitingRoomService.js
/**
 * ClassNX Classroom Waiting Room & Admission Management Service
 * Provides multi-layer synchronization:
 * 1. BroadcastChannel (for instantaneous multi-tab / same-origin communication)
 * 2. Window storage events & LocalStorage persistence
 * 3. Graceful fallback / integration with Backend REST APIs (/api/v2/classroom/rooms/:code/...)
 */

import api from "../api/httpClient";
import { io } from "socket.io-client";

const STORAGE_PREFIX = "classnx:waiting_room:";
const SETTINGS_PREFIX = "classnx:room_settings:";
const BROADCAST_CHANNEL_NAME = "classnx_classroom_waiting_room_channel";
const SIGNALING_URL = process.env.REACT_APP_SIGNALING_URL || "";
const SIGNALING_NAMESPACE = process.env.REACT_APP_SIGNALING_NAMESPACE || "/classroom";

export function normalizeWaitingRoomRequest(request = {}) {
  const rawStatus = String(request.status || "").toLowerCase();
  const status = rawStatus === "waiting" ? "pending" : rawStatus;

  return {
    ...request,
    // Prefer the authoritative server ID over the temporary client ID.
    id: request.requestId || request.request_id || request.id,
    requestId: request.requestId || request.request_id || request.id,
    studentName: request.studentName || request.student_name || request.name || "Guest Student",
    rollOrPhone: request.rollOrPhone || request.roll_or_phone || request.studentRollOrPhone || "Guest Student",
    requestedAt: request.requestedAt || request.requested_at || request.createdAt || request.created_at,
    status,
  };
}

/**
 * Exchange the short-lived waiting-room ticket for the attendance guest token
 * used by LiveKit media and attendance endpoints.
 */
export async function joinAdmittedGuest({ roomCode, studentName, rollOrPhone, ticket }) {
  const cleanCode = String(roomCode || "").trim().toUpperCase();
  const admissionToken = String(ticket || "").trim();
  if (!cleanCode) throw new Error("Meeting code is required.");
  if (!admissionToken) throw new Error("The admission ticket is missing. Please wait for the host to admit you again.");

  const response = await api.post(
    `/api/v2/classroom/rooms/${encodeURIComponent(cleanCode)}/join`,
    {
      studentName: String(studentName || "").trim(),
      rollOrPhone: String(rollOrPhone || "").trim(),
      admissionTicket: admissionToken,
    },
    { skipAuth: true },
  );
  const payload = response?.data?.data ?? response?.data ?? {};
  if (payload.success === false) {
    throw new Error(payload.message || "The classroom join request was rejected.");
  }
  if (!payload.guestToken) {
    throw new Error("The classroom joined without returning an attendance guest token.");
  }
  return payload;
}

// Initialize BroadcastChannel if supported in browser environment
let channelInstance = null;
try {
  if (typeof window !== "undefined" && "BroadcastChannel" in window) {
    channelInstance = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
  }
} catch (err) {
  console.warn("BroadcastChannel not supported or restricted:", err);
}

/**
 * Broadcast event to other tabs & dispatch DOM custom event
 */
function broadcastWaitingRoomEvent(payload) {
  try {
    if (channelInstance) {
      channelInstance.postMessage(payload);
    }
  } catch (err) {
    console.warn("Broadcast postMessage error:", err);
  }

  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("classnx:waiting-room-event", { detail: payload })
      );
    }
  } catch {}
}

/**
 * Retrieve raw requests array from localStorage for a given room
 */
export function getRoomRequests(roomCode) {
  if (!roomCode) return [];
  const clean = String(roomCode).trim().toUpperCase();
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${clean}`);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Save requests array to localStorage
 */
function saveRoomRequests(roomCode, requests) {
  if (!roomCode) return;
  const clean = String(roomCode).trim().toUpperCase();
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${clean}`, JSON.stringify(requests));
  } catch (err) {
    console.error("Failed to save waiting room requests:", err);
  }
}

/**
 * Room Settings (Host Admission requirement)
 */
const DEFAULT_ROOM_SETTINGS = {
  requireAdmission: true,
  allowStudentAudio: true,
  allowStudentVideo: false,
};

export function getRoomSettings(roomCode) {
  if (!roomCode) return { ...DEFAULT_ROOM_SETTINGS };
  const clean = String(roomCode).trim().toUpperCase();
  try {
    const raw = localStorage.getItem(`${SETTINGS_PREFIX}${clean}`);
    if (raw) return { ...DEFAULT_ROOM_SETTINGS, ...JSON.parse(raw) };
  } catch {}
  return { ...DEFAULT_ROOM_SETTINGS }; // Default: Host admission is required
}

export function saveRoomSettings(roomCode, settings) {
  if (!roomCode) return;
  const clean = String(roomCode).trim().toUpperCase();
  try {
    const current = getRoomSettings(clean);
    const updated = { ...current, ...settings };
    localStorage.setItem(`${SETTINGS_PREFIX}${clean}`, JSON.stringify(updated));
    broadcastWaitingRoomEvent({
      type: "ROOM_SETTINGS_UPDATED",
      roomCode: clean,
      settings: updated,
    });
    return updated;
  } catch {
    return { ...DEFAULT_ROOM_SETTINGS };
  }
}

export async function updateRoomMediaSettings(roomCode, settings) {
  const clean = String(roomCode || "").trim().toUpperCase();
  if (!clean) throw new Error("Meeting code is required.");
  const payload = {
    allowStudentAudio: Boolean(settings?.allowStudentAudio),
    allowStudentVideo: Boolean(settings?.allowStudentVideo),
  };
  const response = await api.put(
    `/api/v2/classroom/rooms/${encodeURIComponent(clean)}/waiting-room/settings`,
    payload,
  );
  const data = response?.data?.data ?? response?.data ?? {};
  return saveRoomSettings(clean, { ...payload, ...(data.settings || data) });
}

/**
 * Validate a meeting code against active/scheduled online classes
 */
export async function validateMeetingCode(roomCode) {
  if (!roomCode || typeof roomCode !== "string") {
    return {
      valid: false,
      status: "empty",
      error: "Please enter a Meeting ID / Code.",
      classData: null,
    };
  }

  const clean = roomCode.trim().toUpperCase();

  if (clean.length < 4) {
    return {
      valid: false,
      status: "invalid_format",
      error: "Meeting Code must be at least 4 characters long.",
      classData: null,
    };
  }

  let matched;
  try {
    const response = await api.get(`/api/v2/classroom/rooms/${encodeURIComponent(clean)}/verify`, { skipAuth: true });
    const payload = response?.data?.data ?? response?.data ?? {};
    matched = { ...(payload.room || payload.classData || payload), allowStudentAudio: payload.allowStudentAudio ?? true, allowStudentVideo: payload.allowStudentVideo ?? false };
  } catch (error) {
    return { valid: false, status: "not_found", error: error?.message || `Meeting code "${clean}" was not found.`, classData: null };
  }

  if (!matched) {
    return {
      valid: false,
      status: "not_found",
      error: `Meeting code "${clean}" does not match any scheduled or active session. Please check with your teacher.`,
      classData: null,
    };
  }

  if (matched.status === "ended") {
    return {
      valid: false,
      status: "ended",
      error: `This lecture (${matched.topic}) ended at ${matched.endTime}. New admissions are closed.`,
      classData: matched,
    };
  }

  if (matched.status === "scheduled") {
    return {
      valid: true,
      status: "scheduled",
      message: `Lecture scheduled for ${matched.scheduledDate || "Today"} (${matched.startTime} - ${matched.endTime}).`,
      classData: matched,
    };
  }

  return {
    valid: true,
    status: "live",
    message: `Class is LIVE with ${matched.teacherName}.`,
    classData: matched,
  };
}

/**
 * Request admission to a classroom (Knock)
 */
export async function requestClassroomAdmission({ roomCode, studentName, rollOrPhone, studentId }) {
  const cleanCode = String(roomCode).trim().toUpperCase();
  const cleanName = String(studentName).trim();
  const cleanRollOrPhone = String(rollOrPhone || "").trim();
  const id = studentId || `req-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;

  const settings = getRoomSettings(cleanCode);

  // If host has disabled admission requirement, auto-admit immediately!
  const initialStatus = settings.requireAdmission === false ? "admitted" : "pending";

  const newRequest = {
    id,
    guestToken: null,
    roomCode: cleanCode,
    studentName: cleanName,
    rollOrPhone: cleanRollOrPhone || "Guest Student",
    status: initialStatus, // "pending" | "admitted" | "denied"
    requestedAt: new Date().toISOString(),
    denialReason: null,
  };

  const currentRequests = getRoomRequests(cleanCode);
  // Remove any previous request with the same student ID or matching name
  const filtered = currentRequests.filter(
    (r) => r.id !== id && r.studentName.toLowerCase() !== cleanName.toLowerCase()
  );
  filtered.push(newRequest);
  saveRoomRequests(cleanCode, filtered);

  broadcastWaitingRoomEvent({
    type: "KNOCK_REQUEST",
    roomCode: cleanCode,
    request: newRequest,
  });

  const response = await api.post(`/api/v2/classroom/rooms/${encodeURIComponent(cleanCode)}/waiting-room/request`, {
    requestId: id, studentName: cleanName, rollOrPhone: cleanRollOrPhone,
  }, { skipAuth: true });
  const payload = response?.data?.data ?? response?.data;
  return normalizeWaitingRoomRequest({ ...newRequest, ...(payload?.request || payload || {}) });
}

/**
 * Cancel or withdraw an admission request
 */
export function cancelClassroomAdmission(roomCode, requestId) {
  const cleanCode = String(roomCode).trim().toUpperCase();
  const currentRequests = getRoomRequests(cleanCode);
  const updated = currentRequests.filter((r) => r.id !== requestId);
  saveRoomRequests(cleanCode, updated);

  broadcastWaitingRoomEvent({
    type: "KNOCK_CANCELLED",
    roomCode: cleanCode,
    requestId,
  });

  try {
    api
      .delete(
        `/api/v2/classroom/rooms/${encodeURIComponent(cleanCode)}/waiting-room/request/${encodeURIComponent(
          requestId
        )}`,
        { skipAuth: true }
      )
      .catch(() => {});
  } catch {}
}

/**
 * Admit a student into the live class (Host Action)
 */
export async function admitStudent(roomCode, requestId) {
  const cleanCode = String(roomCode).trim().toUpperCase();
  let serverTicket = null;
  try {
    const res = await api.post(`/api/v2/classroom/rooms/${encodeURIComponent(cleanCode)}/waiting-room/admit`, { requestId });
    const payload = res?.data?.data ?? res?.data ?? {};
    serverTicket = payload?.admissionTicket || payload?.guestToken || payload?.ticket || null;
  } catch { /* proceed with local ticket if backend call fails or in mock */ }

  const currentRequests = getRoomRequests(cleanCode);
  let targetRequest = null;
  const updated = currentRequests.map((req) => {
    if (req.id === requestId) {
      const ticket = serverTicket || req.admissionTicket || req.guestToken || req.ticket || `adm_tkt_${requestId}_${Date.now()}`;
      targetRequest = {
        ...req,
        status: "admitted",
        admittedAt: new Date().toISOString(),
        admissionTicket: ticket,
        guestToken: ticket,
        ticket,
      };
      return targetRequest;
    }
    return req;
  });

  saveRoomRequests(cleanCode, updated);

  const issuedTicket = targetRequest?.admissionTicket || serverTicket || `adm_tkt_${requestId}_${Date.now()}`;

  broadcastWaitingRoomEvent({
    type: "ADMISSION_DECISION",
    roomCode: cleanCode,
    requestId,
    status: "admitted",
    admissionTicket: issuedTicket,
    guestToken: issuedTicket,
    ticket: issuedTicket,
  });

  return targetRequest;
}

/**
 * Admit ALL pending students at once (Host Action)
 */
export async function admitAllStudents(roomCode) {
  const cleanCode = String(roomCode).trim().toUpperCase();
  try {
    await api.post(`/api/v2/classroom/rooms/${encodeURIComponent(cleanCode)}/waiting-room/admit-all`);
  } catch {}

  const currentRequests = getRoomRequests(cleanCode);
  const updated = currentRequests.map((req) => {
    if (req.status === "pending") {
      const ticket = req.admissionTicket || req.guestToken || req.ticket || `adm_tkt_${req.id}_${Date.now()}`;
      return {
        ...req,
        status: "admitted",
        admittedAt: new Date().toISOString(),
        admissionTicket: ticket,
        guestToken: ticket,
        ticket,
      };
    }
    return req;
  });

  saveRoomRequests(cleanCode, updated);

  broadcastWaitingRoomEvent({
    type: "ADMIT_ALL",
    roomCode: cleanCode,
  });

  return updated.filter((r) => r.status === "admitted");
}

/**
 * Deny admission to a student (Host Action)
 */
export async function denyStudent(roomCode, requestId, reason = "Host declined entry request.") {
  const cleanCode = String(roomCode).trim().toUpperCase();
  const currentRequests = getRoomRequests(cleanCode);

  const updated = currentRequests.map((req) => {
    if (req.id === requestId) {
      return {
        ...req,
        status: "denied",
        deniedAt: new Date().toISOString(),
        denialReason: reason,
      };
    }
    return req;
  });

  saveRoomRequests(cleanCode, updated);

  broadcastWaitingRoomEvent({
    type: "ADMISSION_DECISION",
    roomCode: cleanCode,
    requestId,
    status: "denied",
    reason,
  });

  await api.post(`/api/v2/classroom/rooms/${encodeURIComponent(cleanCode)}/waiting-room/deny`, { requestId, reason });
}

/**
 * Student Hook / Subscription: Listen for admission approval or denial
 */
export function subscribeToAdmissionStatus({ roomCode, requestId, onAdmitted, onDenied }) {
  if (!roomCode || !requestId) return () => {};
  const cleanCode = String(roomCode).trim().toUpperCase();

  const checkStatus = () => {
    const list = getRoomRequests(cleanCode);
    const item = list.map(normalizeWaitingRoomRequest).find((r) => r.id === requestId);
    if (!item) return;

    if (item.status === "admitted") {
      if (typeof onAdmitted === "function") onAdmitted(item);
    } else if (item.status === "denied") {
      if (typeof onDenied === "function") onDenied(item.denialReason || "The host declined your entry request.");
    }
  };

  // Immediate check
  checkStatus();

  // 1. BroadcastChannel listener
  const handleBroadcast = (event) => {
    const data = event?.data;
    if (!data || data.roomCode !== cleanCode) return;
    if (data.type === "ADMIT_ALL") {
      checkStatus();
    } else if (data.type === "ADMISSION_DECISION" && data.requestId === requestId) {
      if (data.status === "admitted") {
        onAdmitted?.(data);
      } else if (data.status === "denied") {
        onDenied?.(data.reason || "The host declined your entry request.");
      }
    }
  };

  if (channelInstance) {
    channelInstance.addEventListener("message", handleBroadcast);
  }

  // 2. Custom window event listener
  const handleCustomEvent = (e) => {
    handleBroadcast({ data: e.detail });
  };
  window.addEventListener("classnx:waiting-room-event", handleCustomEvent);

  // 3. Storage event listener (for other tabs)
  const handleStorage = (e) => {
    if (e.key === `${STORAGE_PREFIX}${cleanCode}`) {
      checkStatus();
    }
  };
  window.addEventListener("storage", handleStorage);

  // 4. Polling fallback every 1.5s
  const pollInterval = setInterval(checkStatus, 1500);
  const pollServer = async () => {
    try {
      const response = await api.get(`/api/v2/classroom/rooms/${encodeURIComponent(cleanCode)}/waiting-room/request/${encodeURIComponent(requestId)}?_=${Date.now()}`, {
        skipAuth: true,
      });
      const rawItem = response?.data?.data?.request ?? response?.data?.data ?? response?.data;
      const item = normalizeWaitingRoomRequest(rawItem);
      if (item.status === "admitted") onAdmitted?.(item);
      if (item.status === "denied") onDenied?.(item.denialReason || item.reason || "The host declined your entry request.");
    } catch { /* transient polling failures are retried */ }
  };
  pollServer();
  const serverPollInterval = setInterval(pollServer, 2000);

  return () => {
    if (channelInstance) {
      channelInstance.removeEventListener("message", handleBroadcast);
    }
    window.removeEventListener("classnx:waiting-room-event", handleCustomEvent);
    window.removeEventListener("storage", handleStorage);
    clearInterval(pollInterval);
    clearInterval(serverPollInterval);
  };
}

/**
 * Host Hook / Subscription: Listen for pending knock requests
 */
export function subscribeToWaitingRoomRequests({ roomCode, onUpdate }) {
  if (!roomCode) return () => {};
  const cleanCode = String(roomCode).trim().toUpperCase();

  const publishUpdate = (requests) => {
    const all = requests.map(normalizeWaitingRoomRequest);
    const pending = all.filter((r) => r.status === "pending");
    if (typeof onUpdate === "function") {
      onUpdate({ pending, all });
    }
  };

  const dispatchUpdate = () => publishUpdate(getRoomRequests(cleanCode));

  const pollServer = async () => {
    try {
      const response = await api.get(`/api/v2/classroom/rooms/${encodeURIComponent(cleanCode)}/waiting-room/requests`);
      const payload = response?.data?.data ?? response?.data;
      const requests = Array.isArray(payload) ? payload : payload?.requests || payload?.items || [];
      const all = requests.map(normalizeWaitingRoomRequest);
      saveRoomRequests(cleanCode, all);
      publishUpdate(all);
    } catch { /* preserve the last server result and retry */ }
  };

  // Initial call
  dispatchUpdate();

  const handleEvent = (event) => {
    const data = event?.data || event?.detail;
    if (!data || data.roomCode !== cleanCode) return;
    dispatchUpdate();
  };

  if (channelInstance) {
    channelInstance.addEventListener("message", handleEvent);
  }
  window.addEventListener("classnx:waiting-room-event", handleEvent);

  const handleStorage = (e) => {
    if (e.key === `${STORAGE_PREFIX}${cleanCode}` || e.key === `${SETTINGS_PREFIX}${cleanCode}`) {
      dispatchUpdate();
    }
  };
  window.addEventListener("storage", handleStorage);

  const pollInterval = setInterval(dispatchUpdate, 2000);
  pollServer();
  const serverPollInterval = setInterval(pollServer, 2000);

  // Live host notifications. The authenticated session cookie identifies and
  // authorizes the tuition host; the event is only a prompt to reload the
  // authoritative queue from REST.
  let socket = null;
  let handleSocketConnect = null;
  let handleWaitingRoomChanged = null;

  if (SIGNALING_URL) {
    try {
      const signalingEndpoint = `${SIGNALING_URL.replace(/\/$/, "")}${SIGNALING_NAMESPACE}`;
      socket = io(signalingEndpoint, {
        transports: ["websocket"],
        withCredentials: true,
        query: { roomId: cleanCode, role: "teacher" },
        reconnectionAttempts: 1,
        timeout: 3000,
      });

      handleSocketConnect = () => {
        socket?.emit("class:join", { roomId: cleanCode, role: "teacher" });
      };

      handleWaitingRoomChanged = (event = {}) => {
        const eventRoomCode = event.roomCode || event.meetingCode || event.roomId;
        if (eventRoomCode && String(eventRoomCode).trim().toUpperCase() !== cleanCode) return;
        pollServer();
      };

      const handleConnectError = () => {
        // Signaling server unavailable; disconnect cleanly without continuous retry spam.
        try {
          socket?.disconnect();
        } catch { /* ignore */ }
      };

      socket.on("connect", handleSocketConnect);
      socket.on("class:waiting-room-changed", handleWaitingRoomChanged);
      socket.on("connect_error", handleConnectError);
    } catch {
      socket = null;
    }
  }

  return () => {
    if (channelInstance) {
      channelInstance.removeEventListener("message", handleEvent);
    }
    window.removeEventListener("classnx:waiting-room-event", handleEvent);
    window.removeEventListener("storage", handleStorage);
    clearInterval(pollInterval);
    clearInterval(serverPollInterval);
    if (socket) {
      if (handleSocketConnect) socket.off("connect", handleSocketConnect);
      if (handleWaitingRoomChanged) socket.off("class:waiting-room-changed", handleWaitingRoomChanged);
      socket.off("connect_error");
      try {
        socket.disconnect();
        socket.close();
      } catch { /* ignore */ }
    }
  };
}
